import type { Locale } from '@/shared/i18n'
import type { IsoDateTime, Role, Stage, Uuid } from '@/shared/types/domain'

/**
 * Configuración: los sets de columnas y los usuarios.
 *
 * `StageSetting` extiende el `Stage` del dominio (el mismo que dibuja el
 * tablero) con lo que solo le importa a esta pantalla: a qué set pertenece, su
 * `key` estable y cuántos ítems tiene adentro.
 */

export type StageSetKind = 'project' | 'task'

export interface StageSetting extends Stage {
  stage_set_id: Uuid
  /** Slug estable. No se renombra: el nombre visible es `name`. */
  key: string
  /** La columna donde entra lo que se crea sin especificar fase. */
  is_default_entry: boolean
  /**
   * La columna cierra el trabajo: lo que cae acá se cuenta como terminado.
   *
   * Es un flag aparte de `category` a propósito. La categoría es del sistema
   * (la leen las notificaciones y las métricas) y no se edita; "terminal" es
   * una decisión de tablero que sí puede cambiar —una empresa puede tener
   * "Entregado" y "Cancelado", las dos cerradas— y por eso se edita acá.
   */
  is_terminal: boolean
  /**
   * Cuántos proyectos o tareas viven hoy en esta columna.
   *
   * `StageRead.items_count` viaja siempre, así que el diálogo de borrado puede
   * pedir destino **sólo cuando hay algo que reubicar**. Pedirlo sobre una
   * columna vacía —que es el caso mayoritario— era un paso de más en cada
   * borrado.
   */
  items_count: number
}

export interface StageSet {
  id: Uuid
  /** `null` = plantilla del sistema, compartida por todas las empresas. */
  company_id: Uuid | null
  kind: StageSetKind
  name: string
  is_system: boolean
  stages: StageSetting[]
}

/**
 * Lo que acepta `PATCH /stage-sets/stages/{id}`.
 *
 * `label` y `color_token` son los nombres del backend (acá se llamaban `name` y
 * `color`). **`is_terminal` no existe**: lo que cierra el trabajo es
 * `category === 'done'`, y la categoría sí es editable, pero no desde la
 * edición inline — cambiarla mueve las reglas de notificación de todo lo que
 * hay adentro de la columna.
 */
export interface StagePatchInput {
  label?: string
  color_token?: string | null
  is_default_entry?: boolean
}

/**
 * Usuario visto desde `/settings/usuarios`.
 *
 * `company_ids` no está en `UserSummary` porque a un selector de responsable no
 * le importa; acá es la columna central de la pantalla.
 */
export interface SettingsUser {
  id: Uuid
  email: string
  display_name: string
  role: Role
  is_active: boolean
  /** Solo para `client`: la única empresa que puede ver. */
  client_company_id: Uuid | null
  /**
   * Empresas de un `tech_member`.
   *
   * `null` = la respuesta no las informa (el alta y el patch devuelven
   * `UserRead` pelado). Un array vacío se leería como "no tiene ninguna empresa
   * asignada", que es una afirmación distinta de "no lo sabemos".
   */
  company_ids: Uuid[] | null
  /**
   * El idioma del perfil: lo que decide en qué lengua ve la app esta persona.
   *
   * Obligatorio, porque el backend siempre lo informa (`UserRead` y
   * `UserListItem` lo traen con `NOT NULL DEFAULT 'es'`) y el mock también. Era
   * opcional mientras el dataset de pruebas lo omitía, y esa opcionalidad
   * escondía justo el caso que importa: un `<select>` sin valor se dibuja vacío
   * y no se nota hasta que alguien lo abre.
   */
  locale: Locale
  last_login_at: IsoDateTime | null
}

export interface UserInviteInput {
  email: string
  display_name: string
  role: Role
  company_ids?: Uuid[]
  client_company_id?: Uuid | null
}

export interface UserPatchInput {
  role?: Role
  is_active?: boolean
  /** El idioma del perfil. Es el único campo que alguien puede cambiarse a sí mismo. */
  locale?: Locale
}

/**
 * El rol, en CLAVES del catálogo.
 *
 * El texto no vive acá: vive en `common.role.*`, que es el mismo que usan el
 * selector de responsable y el portal. Un mapa de textos en un `types.ts` es
 * exactamente lo que deja media pantalla en español cuando alguien elige
 * portugués.
 */
export const ROLE_LABEL_KEY: Record<Role, string> = {
  admin: 'common.role.admin',
  tech_member: 'common.role.tech_member',
  client: 'common.role.client',
}

export const ROLE_HINT_KEY: Record<Role, string> = {
  admin: 'settings.roleHint.admin',
  tech_member: 'settings.roleHint.tech_member',
  client: 'settings.roleHint.client',
}

/**
 * Qué significa cada categoría, en palabras.
 *
 * Esto se muestra en la pantalla al lado de la categoría bloqueada: no alcanza
 * con no dejar editarla, hay que explicar por qué. El motor de notificaciones y
 * las métricas preguntan por la CATEGORÍA, nunca por el nombre.
 */
export const CATEGORY_LABEL_KEY: Record<Stage['category'], string> = {
  backlog: 'settings.category.backlog.label',
  active: 'settings.category.active.label',
  review: 'settings.category.review.label',
  blocked: 'settings.category.blocked.label',
  paused: 'settings.category.paused.label',
  done: 'settings.category.done.label',
}

export const CATEGORY_MEANING_KEY: Record<Stage['category'], string> = {
  backlog: 'settings.category.backlog.meaning',
  active: 'settings.category.active.meaning',
  review: 'settings.category.review.meaning',
  blocked: 'settings.category.blocked.meaning',
  paused: 'settings.category.paused.meaning',
  done: 'settings.category.done.meaning',
}

/** Paleta de la columna. `null` = el color de su categoría. El nombre es una CLAVE. */
export const STAGE_COLORS: readonly { value: string; labelKey: string }[] = [
  { value: '--phase-not-started', labelKey: 'settings.color.slate' },
  { value: '--phase-in-development', labelKey: 'settings.color.cyan' },
  { value: '--phase-feedback', labelKey: 'settings.color.violet' },
  { value: '--phase-done', labelKey: 'settings.color.green' },
  { value: '--phase-ongoing', labelKey: 'settings.color.teal' },
  { value: '--phase-paused', labelKey: 'settings.color.gray' },
  { value: '--overdue', labelKey: 'settings.color.red' },
  { value: '--due-soon', labelKey: 'settings.color.amber' },
]

export function stageColor(stage: Pick<StageSetting, 'color' | 'category'>): string {
  const token =
    stage.color ??
    {
      backlog: '--phase-not-started',
      active: '--phase-in-development',
      review: '--phase-feedback',
      blocked: '--overdue',
      paused: '--phase-paused',
      done: '--phase-done',
    }[stage.category]

  return `hsl(var(${token}))`
}

/**
 * Por si una respuesta no informa las membresías.
 *
 * `GET /users` devuelve `UserListItem`, que **sí** las trae; el alta y el patch
 * devuelven `UserRead` pelado, que no. En ese caso la fila dice esto en vez de
 * mostrar una lista vacía, que se leería como "no tiene ninguna empresa".
 */
export const MEMBERSHIPS_UNAVAILABLE_KEY = 'settings.users.membershipsUnavailable'
